import { atom, selector } from 'recoil';
import type { ConvertedFile, OriginalFile, STSSoundSettings, TTSSoundSettings } from '@modules/types/studio';
import type { VoiceModel } from '@modules/types/voice';

export const RType = atom<null | 'file' | 'recording' | 'typing'>({
    key: 'type',
    default: null,
});

export const RSelectedVoice = atom<VoiceModel | null>({
    key: 'selectedVoice',
    default: null,
});

export const ROriginalFile = atom<OriginalFile | null>({
    key: 'originalFile',
    default: null,
});

export const RScript = atom<string>({
    key: 'script',
    default: '',
});

export const RScriptList = atom<{ text: string; intonation: number }[]>({
    key: 'scriptList',
    default: [{ text: '', intonation: 0 }],
});

export const RCharacterCount = selector({
    key: 'characterCount',
    get: ({ get }) => {
        const script = get(RScript);

        return script.length;
    },
});

export const RConvertedFile = atom<ConvertedFile | null>({
    key: 'convertedFile',
    default: null,
});

export const RSTSSettings = atom<STSSoundSettings>({
    key: 'stsSoundSettings',
    default: {
        type: 'STS',
        pitch: 0,
        tempo: 100,
        volume: 1,
        pitchSettingMode: 'auto',
    },
});

export const RTTSSettings = atom<TTSSoundSettings>({
    key: 'ttsSoundSettings',
    default: {
        type: 'TTS',
        version: 2,
        emotion: 'neutral',
        pause: 0.625,
        pitch: 0,
        tempo: 100,
        volume: 1,
        language: 'ko',
    },
});

// export const RSoundSettings = atom<STSSoundSettings | TTSSoundSettings>({
//     key: 'soundSettings',
//     default: {
//         type: 'STS',
//         pitch: 0,
//         tempo: 100,
//         volume: 1,
//         pitchSettingMode: 'auto',
//     },
// });

export const RRecordingScriptList = atom<{
    [key in number]: {
        KR: {
            title: string;
            script: string;
        };
        EN: {
            title: string;
            script: string;
        };
    };
}>({
    key: 'recordingScriptList',
    default: {
        1: {
            KR: {
                title: '어느 기분 좋은 날',
                script: '오늘 아침, 고양이가 책상 위를 툭툭 걸어다녔어요.\n부스럭 소리에 깼더니, 커튼 사이로 햇살이 스며오고 있었죠.\n“벌써 시간이 이렇게 됐어?” 잠결에 중얼거렸어요.\n창문을 여니, 찬 바람 속에서 새소리가 들려왔어요.\n그 순간, 이유 없이 마음이 편안해졌어요.',
            },
            EN: {
                title: 'A gentle start to the day',
                script: 'This morning, I heard soft footsteps on my desk.\n It was my cat, walking quietly, tapping with tiny paws.\n I woke up to the faint rustle of curtains,\n and golden sunlight was streaming through the gaps.\n "Is it that late already?" I murmured, still half-asleep.\n I opened the window, and a chill breeze swept in—\n carrying the sound of birds in the distance.\n In that moment, for no reason at all,\n I felt completely at peace.',
            },
        },
        2: {
            KR: {
                title: '퇴근길의 소소한 기쁨',
                script: '오늘은 유난히 긴 하루였어요.\n버스를 타고 창가 자리에 앉았는데, 노을이 참 예쁘더라고요.\n귓가엔 익숙한 노래가 흐르고 있었고, 바람은 생각보다 시원했어요.\n갑자기 혼자 미소가 지어졌어요.\n“이런 순간도 나쁘지 않네.” 속으로 중얼거렸죠.',
            },
            EN: {
                title: 'A quiet joy on the way home',
                script: 'Today felt longer than usual.\nI got on the bus and found a seat by the window—\nthe sunset was surprisingly beautiful.\nFamiliar music played in my ears,\nand a cool breeze brushed past my face.\n Without thinking, I smiled.\n"This isn\'t so bad after all," I whispered to myself.',
            },
        },
        3: {
            KR: {
                title: '비 오는 날의 회상',
                script: '갑자기 내리기 시작한 비에 발이 다 젖어버렸어요.\n우산을 안 가져온 걸 후회하면서, 오래된 카페로 들어갔죠.\n창밖엔 빗소리가 또르르 또르르, 끊임없이 흐르고 있었어요.\n문득, 예전에 비 오는 날 걷던 골목길이 떠올랐어요.\n참 이상하죠. 그때도 지금처럼 마음이 조용했어요.',
            },
            EN: {
                title: 'Rainy day nostalgia',
                script: 'The rain started falling out of nowhere,\nsoaked my shoes before I knew it.\nI regretted not bringing an umbrella,\nand ducked into an old café on the corner.\nOutside, raindrops tapped the window—steady, soft, never-ending.\nSuddenly, I remembered a rainy alley I used to walk.\nFunny, how quiet my heart feels in moments like this.',
            },
        },
        4: {
            KR: {
                title: '주말 아침의 여유',
                script: '오늘은 알람 없이 천천히 눈을 떴어요.\n커튼 틈 사이로 햇살이 부드럽게 들어왔고,\n주방에선 따뜻한 커피 향이 가득 퍼져 있었어요.\n라디오에선 잔잔한 음악이 흘렀고, 그 순간이 참 좋았어요.\n“이게 바로 여유지.” 나도 모르게 중얼거렸죠.',
            },
            EN: {
                title: 'Slow weekend morning',
                script: 'This morning, I woke up with no alarm.\nSunlight peeked through the curtains, soft and warm.\nThe kitchen smelled like fresh coffee,\nand the radio played a calm, familiar tune.\nEverything felt still.\n“This must be what peace feels like,” I murmured.',
            },
        },
    },
});
